import { cn } from '@heidi/utils';
import type { ReactNode } from 'react';

import type { WorkMode } from '../../contracts/work-mode';

/** Retires the rail so the hero + composer never clip. */
const SHORT_VIEWPORT_HIDE = '[@media(max-height:768px)]:hidden';

export interface WorkHomeExtrasGate {
  workMode: WorkMode;
  /**
   * Whether the Evidence starter slot is live, independent of the page mode —
   * the composer pill can put only the COMPOSER in Evidence. See
   * `WorkHomeBelowComposerProps.showEvidenceStarters`.
   */
  showEvidenceStarters?: boolean;
  isContextFocused: boolean;
  hasStarted: boolean;
}

/**
 * Whether Work's extras — the rotating suggestion tray and the routine rail —
 * are live. They answer to Auto. The pill can put the composer in Evidence while
 * the page mode is still `work`, so it is the starters showing, not the page
 * mode, that stands them down: under an Evidence composer that space belongs to
 * Evidence's own contextual surface, and routines are not contextual to a
 * clinical question.
 *
 * Lives here, with the rail, and is imported by `WorkHomeChrome` for the tray.
 * The two surfaces no longer share an element — the rail sits at the landing's
 * foot, past the page's slack — and two copies of this predicate would let them
 * disagree about which mode they are in.
 */
export const showsWorkHomeExtras = (gate: WorkHomeExtrasGate): boolean => {
  const isEvidence = gate.workMode === 'evidence';
  return (
    !isEvidence &&
    !showsEvidenceStarterSlot(gate) &&
    !gate.isContextFocused &&
    !gate.hasStarted
  );
};

/**
 * Whether the landing is showing Evidence's contextual slot — the inverse half
 * of the predicate above, and the gate the journals footer answers to.
 *
 * Exported rather than re-derived so the two floor surfaces cannot disagree
 * about which mode the landing is in: the rail and the footer are mutually
 * exclusive citizens of the same cluster, and a second copy of this expression
 * would eventually let both render, or neither.
 */
export const showsEvidenceStarterSlot = ({
  workMode,
  showEvidenceStarters,
  isContextFocused,
}: WorkHomeExtrasGate): boolean =>
  (showEvidenceStarters ?? workMode === 'evidence') && !isContextFocused;

export interface WorkHomeRoutineDockProps extends WorkHomeExtrasGate {
  /** Host-supplied routine rail — quarantine-named, so only the app can build it. */
  waysToWork: ReactNode;
}

/**
 * The routine rail, docked at the FOOT of the landing rather than under the
 * composer.
 *
 * It is its own surface rather than a member of `WorkHomeBelowComposer` for a
 * positional reason, not a compositional one: the page collects all of the
 * landing's slack in one `flex-1` spacer placed after the below-composer chrome,
 * and the rail has to sit on the far side of that spacer to reach the bottom of
 * the canvas. Nested inside the chrome it could only ever sit directly beneath
 * the composer's suggestions, which is where it used to be.
 *
 * The rail is what yields on a short window, and it has to be the rail, because
 * its height is the one in this column that varies BY MODE. The top inset is
 * shrinkable — that is how the landing survives a short viewport at all — and
 * the floor spacer's `basis` is already 0, so it has nothing to give. A
 * `shrink-0` rail therefore pushes its whole height into the inset, and only in
 * Auto, where the rail renders: measured at 1512x772 that collapsed the inset
 * from 139px to 15px and jammed the composer against the top of the canvas,
 * while Evidence — no rail, no squeeze — kept its full offset. That is up to
 * ~124px of Auto/Evidence drift at every height under ~924px, which is the
 * mode-dependent placement the top anchor exists to delete.
 *
 * Under AGE-1870's bottom-anchored layout this relocation would instead have
 * dragged the composer up by the rail's height at every viewport.
 */
export const WorkHomeRoutineDock = ({
  waysToWork,
  ...gate
}: WorkHomeRoutineDockProps): JSX.Element | null =>
  showsWorkHomeExtras(gate) ? (
    // Flex splits a deficit in proportion to basis x factor, so weighting the
    // rail 20x against the inset makes it absorb ~97% of any squeeze and leaves
    // the inset the last member to give — it stays shrinkable for the deficit
    // the rail cannot cover, and below 768px it is the only one left.
    // `min-h-0` is load-bearing: the default `min-height: auto` floors a flex
    // item at its content height, so without it the factor never engages. The
    // carousel then clips at the canvas edge, which the cluster's ancestor
    // `overflow-hidden` already contains.
    <div
      className={cn('min-h-0 w-full shrink-[20] pb-6', SHORT_VIEWPORT_HIDE)}
      data-testid="work-home-routine-dock"
    >
      {waysToWork}
    </div>
  ) : null;
